import { ApolloClient } from 'apollo-client'
import { ApolloLink } from 'apollo-link'
import { HttpLink } from 'apollo-link-http'
import { InMemoryCache } from 'apollo-cache-inmemory'
import Vue from 'vue'
import VueApollo from 'vue-apollo'
import fetch from 'unfetch'
import { ApolloLink as ApolloLinkBase, Observable } from 'apollo-link'

Vue.use(VueApollo)

const uri = `${process.env.VUE_APP_SERVER_URL}/graphql`

const CACHE_TTL = 10 * 60 * 1000 // 10 minutes in milliseconds

// A lightweight query that is NEVER cached, used to check event status.
// const triggerQuery = 'getEventBasicData'
// Heavier queries that ARE cached for performance.
const queriesToCache = [
  'getEventAdditionalDataForRegisterContainer',
  'getEventDetailsForRegisterContainer',
  'getOnlineOrderItems',
  'getEventFloorPlanAndOrderItems',
  'getEventNumOfRemainingTickets',
  'getLanguageCodesForEvent',
]

const cacheLink = new ApolloLinkBase((operation, forward) => {
  const { operationName, variables } = operation

  // 1. If this is a query we can cache, try to serve it from localStorage.
  if (queriesToCache.includes(operationName) && variables) {
    let slug = variables.slug
    // If no slug is in the variables, try to find a redirect using the eventId.
    if (!slug && variables.eventId) {
      const slugFromRedirect = window.localStorage.getItem(`redirect:${variables.eventId}`)
      if (slugFromRedirect) {
        slug = slugFromRedirect
      }
    }

    if (slug) {
      const key = `${operationName}:${slug}`
      const cachedRaw = window.localStorage.getItem(key)
      if (cachedRaw) {
        try {
          const { timestamp, expiresAt, data } = JSON.parse(cachedRaw)
          const isStillValid = expiresAt
            ? Date.now() < expiresAt
            : Date.now() - timestamp < CACHE_TTL

          if (isStillValid) {
            return new Observable(observer => {
              observer.next({ data })
              observer.complete()
            })
          } else {
            window.localStorage.removeItem(key)
          }
        } catch (error) {
          console.warn('Error reading cache for', key, error)
          window.localStorage.removeItem(key)
        }
      }
    }
  }

  // 2. For the trigger query, or for cache misses, let the request go to the network.
  return forward(operation).map(response => {
    // Re-destructure here to be safe
    const { operationName, variables } = operation

    // We only perform caching logic if the request was successful
    if (!response.errors && response.data) {
      // Find the canonical slug from the response data, as this is the single source of truth.
      let canonicalSlug = null
      if (
        operationName === 'getEventFloorPlanAndOrderItems' &&
        response.data.getEventFloorPlanAndOrderItems
      ) {
        canonicalSlug = response.data.getEventFloorPlanAndOrderItems.slug
      } else if (
        operationName === 'getOnlineOrderItems' &&
        variables.slug // This query requires the slug in variables.
      ) {
        canonicalSlug = variables.slug
      } else if (
        operationName === 'getEventDetailsForRegisterContainer' &&
        response.data.getEventDetailsForRegisterContainer
      ) {
        canonicalSlug = response.data.getEventDetailsForRegisterContainer.slug
      } else if (
        operationName === 'getEventAdditionalDataForRegisterContainer' &&
        response.data.getEventAdditionalDataForRegisterContainer
      ) {
        canonicalSlug = response.data.getEventAdditionalDataForRegisterContainer.slug
      } else if (operationName === 'getEventBasicData' && response.data.getEvent) {
        canonicalSlug = response.data.getEvent.slug
      } else if (
        operationName === 'getLanguageCodesForEvent' &&
        response.data.getLanguageCodesForEvent
      ) {
        // get slug from query variables
        canonicalSlug = variables.slug
      }

      // If we couldn't find a slug in the response, fall back to checking the redirect table.
      if (!canonicalSlug && variables && variables.eventId) {
        canonicalSlug = window.localStorage.getItem(`redirect:${variables.eventId}`)
      }

      // Only proceed if we have a slug to work with.
      if (canonicalSlug) {
        // If the original request was by eventId, create the redirect.
        if (variables && variables.eventId && !variables.slug) {
          window.localStorage.setItem(`redirect:${variables.eventId}`, canonicalSlug)
        }

        // 3. If the response is for our trigger query...
        if (
          operationName === 'getEventBasicData' &&
          response.data.getEvent &&
          response.data.getEvent.salesOpened
        ) {
          // If sales have opened, purge the cache for related queries to force a refresh.
          window.localStorage.removeItem(`getOnlineOrderItems:${canonicalSlug}`)
          window.localStorage.removeItem(`getEventFloorPlanAndOrderItems:${canonicalSlug}`)
          window.localStorage.removeItem(`getEventNumOfRemainingTickets:${canonicalSlug}`)
          window.localStorage.removeItem(`getEventDetailsForRegisterContainer:${canonicalSlug}`)
          window.localStorage.removeItem(
            `getEventAdditionalDataForRegisterContainer:${canonicalSlug}`,
          )
        }
        // 4. If the response is for a query that should be cached...
        else if (queriesToCache.includes(operationName)) {
          const dynamicCacheQueries = ['getOnlineOrderItems', 'getEventFloorPlanAndOrderItems']
          const key = `${operationName}:${canonicalSlug}`

          // Special, one-time, dynamic TTL caching for queries with order items.
          if (dynamicCacheQueries.includes(operationName)) {
            const now = new Date()
            let items = null

            if (
              operationName === 'getOnlineOrderItems' &&
              response.data &&
              response.data.getOnlineOrderItems
            ) {
              items = response.data.getOnlineOrderItems
            } else if (
              operationName === 'getEventFloorPlanAndOrderItems' &&
              response.data &&
              response.data.getEventFloorPlanAndOrderItems &&
              response.data.getEventFloorPlanAndOrderItems.orderItems
            ) {
              items = response.data.getEventFloorPlanAndOrderItems.orderItems
            }

            // If we found items, we can apply the caching logic.
            if (items) {
              const itemsOnSale = items.filter(item => {
                if (!item.from || !item.to) return false
                const from = new Date(item.from)
                const to = new Date(item.to)

                return from <= now && now <= to
              })

              // We only cache if NOTHING is currently on sale.
              if (itemsOnSale.length === 0) {
                const upcomingItems = items.filter(item => item.from && new Date(item.from) > now)

                if (upcomingItems.length > 0) {
                  // If there are future sales, cache until 15s before the next one starts.
                  const soonestFrom = Math.min(
                    ...upcomingItems.map(item => new Date(item.from).getTime()),
                  )
                  const expiresAt = soonestFrom - 15000 // 15 seconds before

                  if (expiresAt > Date.now()) {
                    window.localStorage.setItem(
                      key,
                      JSON.stringify({ expiresAt, data: response.data }),
                    )
                  }
                } else {
                  // Nothing on sale now, nothing scheduled for the future. Sales are over or all closed.
                  // Cache with a standard TTL.
                  window.localStorage.setItem(
                    key,
                    JSON.stringify({ timestamp: Date.now(), data: response.data }),
                  )
                }
              }
            }
          } else {
            // Standard 10-minute TTL for other cacheable queries.
            window.localStorage.setItem(
              key,
              JSON.stringify({ timestamp: Date.now(), data: response.data }),
            )
          }
        }
      }
    }

    return response
  })
})

const httpLink = new HttpLink({ uri, fetch, credentials: 'include' })

const link = ApolloLink.from([cacheLink, httpLink])

const cache = new InMemoryCache({
  addTypename: true,
})

// persistCache({
//   cache,
//   storage: window.localStorage,
// })

export const apolloClient = new ApolloClient({
  link,
  cache,
  connectToDevTools: true,
  defaultOptions: {
    watchQuery: { fetchPolicy: 'no-cache', errorPolicy: 'ignore' },
    query: { fetchPolicy: 'no-cache', errorPolicy: 'all' },
    mutate: {
      errorPolicy: 'all',
    },
  },
})

export default new VueApollo({
  defaultClient: apolloClient,
})
